import { faCircleStop, faClone, faRightLeft } from "@fortawesome/pro-duotone-svg-icons"
import { faCirclePause, faCirclePlay } from "@fortawesome/pro-regular-svg-icons"
import { faRocketLaunch, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { CAMPAIGN_STATUSES, ICampaign } from "campaign/model"
import { cancelCampaign } from "campaign/tools/cancel"
import { isCampaignPausable, isCampaignStopable } from "campaign/tools/common"
import { deleteCampaign } from "campaign/tools/delete"
import { toggleDspDealStatus } from "campaign/tools/dsp-deal"
import { openDuplicatePopup } from "campaign/tools/duplicate"
import { setCampaignPause } from "campaign/tools/save"
import { IForm } from "form/model"
import { formActionCreator } from "form/store/actions"
import { messageSVC } from "message/service"
import { IActionButton } from "ui/model"
import { getForm, setIsPausedInForm } from "./common"
import { isCampaignLaunchableInFormContext, launchCampaign, translatedLaunchError } from "./launch"

export function getFormActionButtons(form: IForm, isSummary: boolean): IActionButton[] {
  const actionButtons: IActionButton[] = []
  const { isCreation, isForecast, isDspDeal, isPaused } = form.context
  if (isCreation) {
    return actionButtons
  }
  const statusId = form.statusId
  const canDelete = isForecast || statusId === CAMPAIGN_STATUSES.PENDING
  const canActivate = isDspDeal && statusId !== CAMPAIGN_STATUSES.EXPIRED
  const canDuplicate = !isDspDeal
  const canStop = !isForecast && !isDspDeal && isCampaignStopable(statusId)
  const canLaunch = !isForecast && isCampaignLaunchableInFormContext(form.context.appContext, statusId)
  const launchError = form ? translatedLaunchError(form) : ""
  const canPause = !isForecast && isCampaignPausable(statusId, isForecast, isDspDeal)
  const campaign = form.context.originalCampaign as ICampaign
  if (canDelete) {
    actionButtons.push({
      label: translate("label.delete"),
      icon: faTrash,
      onClick: () => deleteCampaign(form.campaignId, campaign.product || "-", isForecast)
    })
  }
  if (canStop) {
    actionButtons.push({
      id: "form-action-edit-button",
      label: translate("button.stop"),
      icon: faCircleStop,
      onClick: async () =>
        await cancelCampaign(campaign.id, campaign.product || "-", isForecast)
          .then(hasChanged => {
            if (hasChanged) {
              getStore().dispatch(
                formActionCreator.setForm({ form: { ...getForm(), statusId: CAMPAIGN_STATUSES.CANCELLED } })
              )
            }
          })
          .catch(error => {
            console.error(error)
            messageSVC.error("campaign.pause.error")
          })
    })
  }
  if (canPause) {
    actionButtons.push({
      id: "form-action-pause-button",
      label: translate(isPaused ? "campaign.unpause.button" : "campaign.pause.button"),
      icon: isPaused ? faCirclePlay : faCirclePause,
      onClick: async () => {
        const newIsPaused = !isPaused
        return setCampaignPause(campaign.id, campaign.product || "", newIsPaused, true)
          .then(hasChanged => {
            if (hasChanged) {
              setIsPausedInForm(form, newIsPaused)
            }
          })
          .catch(error => {
            console.error(error)
            messageSVC.error("campaign.pause.error")
          })
      }
    })
  }
  if (isSummary) {
    if (canDuplicate) {
      actionButtons.push({
        id: "form-action-duplicate-button",
        label: translate("button.duplicate"),
        icon: faClone,
        onClick: () => openDuplicatePopup(campaign)
      })
    }
    if (isForecast) {
      actionButtons.push({
        id: "form-action-convert-button",
        label: translate("forecast.convert.button"),
        icon: faRightLeft,
        onClick: () => openDuplicatePopup(campaign, true)
      })
    } else {
      if (canLaunch) {
        actionButtons.push({
          id: "form-action-launch-button",
          label: translate("button.launch"),
          disabled: launchError !== "",
          icon: faRocketLaunch,
          onClick: async () => launchCampaign()
        })
      }
    }
    if (canActivate) {
      const isActive = statusId === CAMPAIGN_STATUSES.ACTIVE
      actionButtons.push({
        id: "form-action-activate-button",
        label: translate(isActive ? "button.deactivate" : "button.activate"),
        icon: isActive ? faCirclePause : faRocketLaunch,
        onClick: async () =>
          await toggleDspDealStatus(campaign, false)
            .then(newStatus => {
              if (newStatus) {
                const form = getForm()
                getStore().dispatch(
                  formActionCreator.setForm({
                    form: {
                      ...form,
                      context: {
                        ...form.context,
                        originalCampaign: { ...(form.context.originalCampaign as ICampaign), status_id: newStatus }
                      },
                      statusId: newStatus
                    }
                  })
                )
              }
            })
            .catch(error => {
              console.error(error)
              messageSVC.error("dsp-deal.activate.error")
            })
      })
    }
  }
  return actionButtons
}
